import { ApplicationResource, RequestedDocuments } from '@applyboard/school-applications-types-lib'
import { SetAllFieldsOptional } from '@applyboard/school-backend-lib'
import { RawApplicationAggregationResponse, useUpdateApplication } from '.'
import { DocumentTags } from '../utils/enums'
import { useToasts } from '@applyboard/crystal-ui'
import { useQueryClient } from '@tanstack/react-query'
import { UploadFileData } from '../components/Application'

type UpdateRequestedDocumentsProps = {
  applicationId: string
  index: number
}

type UpdateRequestedDocumentsParams = {
  requestedDocumentsId: string
  requestedDocuments: SetAllFieldsOptional<RequestedDocuments[string]>
  documentType: DocumentTags
  dataFiles: Record<string, UploadFileData | null>
  additionalData?: SetAllFieldsOptional<Omit<ApplicationResource['attributes'], 'files'>>
  onSuccess: () => void
  sectionReference?: string
}

export function useUpdateRequestedDocuments(props: UpdateRequestedDocumentsProps) {
  const queryClient = useQueryClient()
  const toast = useToasts()
  const { isUpdatingApplication, updateApplication } = useUpdateApplication({
    id: props.applicationId,
  })

  const updateRequestedDocuments = (params: UpdateRequestedDocumentsParams) => {
    const documents = params.requestedDocuments?.documents?.map((doc, index) => {
      if (index === props.index && (doc?.type as string) === params.documentType) {
        // should we only do this after successful upload?
        const fileDataIds = params.dataFiles ? Object.keys(params.dataFiles) : undefined

        return {
          ...doc,
          fileDataIds,
          sectionReference: params.sectionReference || doc?.sectionReference,
        }
      }

      return doc
    })

    if (documents) {
      const attachment = Object.fromEntries(
        Object.entries(params.dataFiles).filter(([_, value]) => value !== null),
      ) as Record<string, UploadFileData>

      const updatePayload: { attributes: any; files?: Record<string, UploadFileData> } = {
        attributes: {
          ...(params.additionalData ?? {}),
          requestedDocuments: {
            [params.requestedDocumentsId]: {
              documents,
            },
          },
        },
      }
      if (Object.keys(attachment).length > 0) {
        updatePayload.files = attachment
      }
      updateApplication(updatePayload, {
        onSuccess: response => {
          queryClient.setQueryData(
            ['applications', response.data.id],
            (oldData: RawApplicationAggregationResponse): RawApplicationAggregationResponse => {
              return {
                data: {
                  ...oldData.data,
                  attributes: {
                    ...oldData.data.attributes,
                    application: response.data,
                  },
                },
              }
            },
          )

          toast.positive('Request updated successfully')

          params.onSuccess()
        },
        onError: err => {
          if (err instanceof Error) {
            if (
              err.status?.toString() === '400' &&
              !!err.message.match(
                /^(Cannot modify document request )(\S*?)( while its status is )(?!PENDING).*$/i,
              )
            ) {
              queryClient.invalidateQueries({
                queryKey: ['applications', props.applicationId],
              })

              params.onSuccess()
            }

            toast.negative(new Error(err.message))
          }
        },
      })
    } else {
      toast.negative(new Error('An error occurred'))
    }
  }

  return {
    isUpdatingApplication,
    updateRequestedDocuments,
  }
}
